import React from "react"
import {
  Toast,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
} from "@/components/ui/toast"
import { useToast } from "@/hooks/use-toast"
import { hasGlobalPermission } from "@/contexts/auth-context"
import { permissions } from "@/lib/permissions"
import { routes, entityDetailRoute } from "@/lib/routes"
import { useI18n } from "@/i18n"
import { renderInlineMarkup } from "@/components/shared/InlineMarkup"
import type { EntityLink } from "@/types/entity-link"

/**
 * Generic entity-link parser for toast / banner / message text. Any backend
 * message can embed an inline link as a JSON object inside double braces. The
 * payload is the canonical {@link EntityLink} shape (matches the backend
 * {@code com.irian.iam.shared.api.EntityLink} record):
 *
 *   {{ {"id":"<uuid>","displayName":"John Doe","entityType":"IDENTITY","subType":"WORKFORCE"} }}
 *
 * The renderer replaces each such block with an anchor; everything outside the
 * blocks is rendered as plain text. The format is entity-agnostic — the only
 * coupling is the entity-type → route mapping in {@link entityDetailRoute}.
 * Any error code, validation message, or notification can use the same token.
 */
const ENTITY_LINK_TOKEN = /\{\{\s*(\{[\s\S]*?\})\s*\}\}/g

function renderEntityLinks(text: string): React.ReactNode {
    if (!text.includes("{{")) return renderInlineMarkup(text)
    const parts: React.ReactNode[] = []
    let lastIndex = 0
    let match: RegExpExecArray | null
    ENTITY_LINK_TOKEN.lastIndex = 0
    while ((match = ENTITY_LINK_TOKEN.exec(text)) !== null) {
        if (match.index > lastIndex) {
            parts.push(renderInlineMarkup(text.slice(lastIndex, match.index)))
        }
        const node = parseEntityToken(match[1])
        parts.push(node ?? match[0])
        lastIndex = match.index + match[0].length
    }
    if (lastIndex < text.length) parts.push(renderInlineMarkup(text.slice(lastIndex)))
    return parts.map((p, i) => <React.Fragment key={i}>{p}</React.Fragment>)
}

function parseEntityToken(json: string): React.ReactNode | null {
    let payload: Partial<EntityLink>
    try {
        payload = JSON.parse(json) as Partial<EntityLink>
    } catch {
        return null
    }
    if (!payload.entityType || !payload.id || !payload.displayName) return null
    const url = entityDetailRoute(payload.entityType, payload.id)
    if (!url) return payload.displayName
    return (
        <a href={url}
           className="underline font-medium"
           data-testid="toast-entity-link">
            {payload.displayName}
        </a>
    )
}

export function Toaster() {
  const { toasts } = useToast()
  const i18n = useI18n()

  return (
    <ToastProvider>
      {toasts.map(({ id, title, description, action, errorLogUrl, duration, ...props }) => {
        const showErrorLogLink = props.variant === 'destructive'
            && hasGlobalPermission(permissions.errorLogs.view)
        const resolvedUrl = errorLogUrl ?? routes.admin.errorLogs.list()
        return (
          <Toast key={id} duration={duration} data-testid={props.variant === 'destructive' ? 'toast-error' : 'toast'} {...props}>
            <div className="grid gap-1">
              {title && <ToastTitle>{title}</ToastTitle>}
              {description && (
              <ToastDescription>{renderEntityLinks(description)}</ToastDescription>
            )}
              {showErrorLogLink && (
                <a href={resolvedUrl}
                   className="text-xs underline opacity-70 hover:opacity-100 mt-1"
                   data-testid="toast-error-log-link">
                  {i18n.common.viewErrorLog}
                </a>
              )}
            </div>
            {action}
            <ToastClose />
          </Toast>
        )
      })}
      <ToastViewport />
    </ToastProvider>
  )
}
